import React, { useEffect, useRef, useState } from "react";
import Reveal from "../common/Reveal";
import gsap from "gsap";
import aboutus from "../../assets/aboutus.png";

// Import layout components
import Header from "../header/LandingHeader";
import Footer from "../footer/Footer";

// Import background images
import HeroBg from "../../assets/hero.webp";
import spiritWordmark from "../../assets/Group 1000010883.png";
import spiritMark from "../../assets/Spirit vector (1).png";

/* Animated count-up — ticks from 0 to the target when scrolled into view. */
const CountUp = ({ value, duration = 1600, className = "" }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const startedRef = useRef(false);

  const match = String(value).match(/^(\D*)([\d,]+)(.*)$/);
  const prefix = match ? match[1] : "";
  const numericStr = match ? match[2] : "0";
  const suffix = match ? match[3] : "";
  const target = parseInt(numericStr.replace(/,/g, ""), 10) || 0;
  const useGrouping = numericStr.includes(",");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || startedRef.current) return;
        startedRef.current = true;
        io.unobserve(el);
        let raf;
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
          setDisplay(Math.round(eased * target));
          if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);

  const formatted = useGrouping
    ? display.toLocaleString("en-US")
    : String(display);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

const IMPACT = [
  { value: "1000+", label: "Participants" },
  { value: "100+", label: "Colleges" },
  { value: "30+", label: "Sporting Events" },
];

const HIGHLIGHTS = [
  {
    id: "01",
    title: "National-Level Competition",
    description:
      "Compete against talented athletes from colleges and universities across India in a professionally organized sporting environment.",
    widthClass: "xl:w-[472px] lg:w-[420px]",
  },
  {
    id: "02",
    title: "30+ Sporting Events",
    description:
      "From football, cricket, basketball, athletics, badminton, volleyball, swimming, and chess to many more individual and team events.",
    widthClass: "xl:w-[322px] lg:w-[280px]",
  },
  {
    id: "03",
    title: "Beyond Sports",
    description:
      "Spirit extends beyond the playing arena with spectacular pronites, live performances, entertainment events, and guest sessions.",
    widthClass: "xl:w-[385px] lg:w-[340px]",
  },
  {
    id: "04",
    title: "Community & Sportsmanship",
    description:
      "At its heart, Spirit celebrates respect, teamwork, discipline, and friendships that continue long after the final whistle.",
    widthClass: "xl:w-[409px] lg:w-[360px]",
  },
];

const AboutSpiritPage = () => {
  const tickerRef = useRef(null);

  useEffect(() => {
    const ticker = tickerRef.current;
    if (!ticker) return;

    const animation = gsap.to(ticker, {
      xPercent: -50,
      duration: 20,
      ease: "linear",
      repeat: -1,
    });

    return () => animation.kill();
  }, []);

  return (
    <div className="w-full overflow-x-hidden font-kumbh text-[#131110] bg-transparent">
      {/* ============================= HERO SECTION ============================= */}
{/* ============================= HERO SECTION ============================= */}
<section
  className="relative h-screen overflow-hidden bg-cover bg-center bg-no-repeat text-white max-md:h-auto max-md:min-h-[100dvh]"
  style={{ backgroundImage: `url(${HeroBg})` }}
>
  {/* Header is positioned absolute to not affect vertical flow */}
  <div className="absolute top-0 left-0 w-full z-20">
    <Header />
  </div>

  {/* Flex container to center the content block vertically */}
  <div className="h-full flex flex-col justify-center px-6 md:px-20 pt-20">
    <div className="max-w-[1400px] mx-auto w-full">
      
      {/* About Us Image - Left Aligned */}
      <Reveal className="w-full max-w-[1375px]">
        <img
          src={aboutus}
          alt="About Us 2026"
          className="w-full h-auto max-w-[1375px] object-contain select-none pointer-events-none"
        />
      </Reveal>

      {/* Description Block - Positioned immediately under the image */}
      <Reveal
        delay={150}
        className="max-w-[720px] mt-10 text-left"
      >
        <p className="font-montserrat text-xs sm:text-sm md:text-[15px] leading-relaxed text-white/90 font-medium tracking-wide">
          With the vision to bring out the leader in you, IIT Guwahati
          Spirit presents the Campus Ambassador Program. With the vision to
          bring out the leader in you, IIT Guwahati Spirit presents the
          Campus Ambassador Program.
        </p>
      </Reveal>
      
    </div>
  </div>
</section>

      {/* ============================= SPONSOR MARQUEE ============================= */}
      <div className="relative z-20 -rotate-1 bg-white py-2 overflow-hidden -my-6 ">
        <div ref={tickerRef} className="flex items-center whitespace-nowrap w-max">
          {/* Flat list with a uniform right margin on every mark, so the gaps
              are identical everywhere -- including at the -50% loop point. */}
          {[...Array(12)].flatMap((_, i) => [
            <img
              key={`wordmark-${i}`}
              src={spiritWordmark}
              alt="Spirit '26"
              className="h-5 w-auto mr-12 object-contain select-none pointer-events-none"
              draggable="false"
            />,
            <img
              key={`mark-${i}`}
              src={spiritMark}
              alt=""
              aria-hidden="true"
              className="h-4 w-auto mr-12 object-contain select-none pointer-events-none"
              draggable="false"
            />,
          ])}
        </div>
      </div>

      {/* ============================= INTRO GRID ============================= */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-16 pt-24 pb-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-5 space-y-6">
          <p className="text-lg sm:text-xl md:text-[22px] font-medium leading-[1.6] text-neutral-700 tracking-wide max-w-sm">
            Hub support people from all over the world
          </p>
          {/* Replace the anchor tags in the "Intro Grid" section */}
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-[15px] font-bold uppercase tracking-widest text-[#65706E]/70">
            <button
              onClick={() => window.open("https://www.youtube.com/@spiritiitguwahati2541", "_blank")}
              className="hover:text-[#cc5e15] transition-colors bg-transparent border-none p-0 cursor-pointer"
            >
              • YouTube
            </button>
            <button
              onClick={() => window.open("https://www.instagram.com/spirit_iitguwahati/", "_blank")}
              className="hover:text-[#cc5e15] transition-colors bg-transparent border-none p-0 cursor-pointer"
            >
              • Instagram
            </button>
            <button
              onClick={() => window.open("https://www.linkedin.com/company/spiritiitguwahati/", "_blank")}
              className="hover:text-[#cc5e15] transition-colors bg-transparent border-none p-0 cursor-pointer"
            >
              • Linkedin
            </button>
          </div>
        </div>

        <div className="md:col-span-7">
          <Reveal>
            <p className="text-lg sm:text-xl md:text-[22px] font-medium leading-[1.6] text-neutral-700 tracking-wide">
              Every year, thousands of participants from over a hundred
              institutions compete across a diverse range of sporting events
              while celebrating the true values of{" "}
              <span className="text-[#cc5e15] font-semibold">TEAMWORK</span>,{" "}
              <span className="text-[#cc5e15] font-semibold">RESILIENCE</span>,
              and{" "}
              <span className="text-[#cc5e15] font-semibold">
                SPORTSMANSHIP
              </span>
              .
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============================= STATS COUNTER ============================= */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-16 py-8 border-b border-[#e5ded4]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-6">
          {IMPACT.map((stat, index) => (
            <Reveal key={stat.label} delay={index * 100} className="space-y-4">
              <div className="border-b-2 border-[#cc5e15]/30 pb-6 group">
                <CountUp
                  value={stat.value}
                  className="font-barlow font-bold text-[64px] md:text-[80px] leading-none text-[#131110]"
                />
                <p className="text-xs font-bold uppercase tracking-[2px] text-[#65706E] mt-3 flex items-center gap-2">
                  <span className="text-[#cc5e15]">•</span> {stat.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===================== WHAT MAKES US APART (EXACT WIDTHS & BORDERS) ===================== */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-16 py-20 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          {/* Left Title Area */}
          <div className="lg:col-span-4 space-y-6 lg:pr-8">
            <Reveal>
              <h2 className="font-barlow font-bold text-[36px] md:text-[42px] text-[#131110] leading-[1.1] tracking-tight">
                Four things that set <br /> it apart.
              </h2>
            </Reveal>
            <div className="space-y-4 font-montserrat text-xs md:text-[13px] text-[#65706E] leading-[1.7] max-w-[320px]">
              <p>
                For over a decade, Spirit has grown into more than a competition
                a national platform hosted on the picturesque IIT Guwahati
                campus, where thousands of participants challenge their limits,
                represent their institutions with pride, and celebrate
                excellence, determination, and unity, both on and off the field.
              </p>
              <p className="font-bold text-[#131110] tracking-normal pt-2">
                A decade of sportsmanship, on and off the field.
              </p>
            </div>
          </div>

          {/* Right Cards Area - Update this block */}
<div className="lg:col-span-8 flex flex-row flex-wrap gap-6 w-full max-w-full">
  {HIGHLIGHTS.map((item, index) => (
    <div key={item.id} className={`flex w-full ${item.widthClass} max-w-full bg-[#F5F0E8]`}>
      <Reveal delay={index * 100} className="h-full w-full">
        <div className="relative overflow-hidden p-8 pt-[160px] border border-[#cc5e15]/30 flex flex-col group cursor-pointer h-full w-full  hover:bg-white/50 transition-colors duration-300">
          <span className="absolute -top-3 -left-9 text-[180px] font-barlow font-bold text-[#cc5e15]/20 leading-none z-0 group-hover:text-[#cc5e15]/70 transition-colors duration-300 select-none">
            {item.id}
          </span>
          <div className="relative z-10 mt-auto">
            <h3 className="text-[18px] font-bold font-barlow text-[#131110] mb-3 tracking-wide">
              {item.title}
            </h3>
            <p className="text-[14px] font-montserrat text-[#65706E] leading-[1.6]">
              {item.description}
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  ))}
</div>
        </div>
      </section>

      {/* ========================= MISSION & VISION ========================= */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-16 py-16 md:py-32 flex flex-col gap-20">
        {/* Heading Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-barlow font-bold text-[44px] md:text-[56px] text-[#131110] leading-[1.05] tracking-tight">
                A Legacy <br /> Built to Last
              </h2>
            </Reveal>
            <Reveal delay={100}>
              <p className="text-[15px] md:text-[17px] text-[#cc5e15]/70 mt-6 leading-relaxed max-w-[280px]">
                Not just a festival a legacy that keeps inspiring every
                generation.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Mission Row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mt-8">
          <div className="lg:col-span-4 xl:col-span-3">
            <Reveal>
              <div className="border-b-[3px] border-[#cc5e15] pb-3 w-full max-w-[280px]">
                <h4 className="font-barlow font-bold text-[18px] md:text-[20px] uppercase tracking-[1px] text-[#cc5e15]">
                  MISSION
                </h4>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8 xl:col-span-9 lg:pl-10">
            <Reveal delay={100}>
              <p className="text-[16px] md:text-[18px] leading-[1.8] font-montserrat text-[#131110]">
                For over a decade, Spirit has grown into more than a competition
                a national platform hosted on the picturesque IIT Guwahati
                campus.{" "}
                <span className="text-[#65706E]/50">
                  Thousands of participants have challenged their limits,
                  represented their institutions with pride, and celebrated{" "}
                </span>
                <span className="text-[#cc5e15]">
                  excellence, determination, and unity
                </span>
                {", "}both on and off the field.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Vision Row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-4 xl:col-span-3">
            <Reveal>
              <div className="border-b-[3px] border-[#cc5e15] pb-3 w-full max-w-[280px]">
                <h4 className="font-barlow font-bold text-[18px] md:text-[20px] uppercase tracking-[1px] text-[#cc5e15]">
                  VISION
                </h4>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8 xl:col-span-9 lg:pl-10">
            <Reveal delay={100}>
              <p className="text-[16px] md:text-[18px] leading-[1.8] font-montserrat text-[#131110]">
                To establish Spirit as one of India's most prestigious
                inter-collegiate sports festivals{" "}
                <span className="text-[#65706E]/50">
                  by continuously raising the standards of competition,
                  innovation, organization, and participant experience while
                  promoting a culture of{" "}
                </span>
                <span className="text-[#cc5e15]">sportsmanship and unity.</span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutSpiritPage;
